if (!customElements.get('wlsn-aos-observer')) {
  class WLSNAOSObserver extends HTMLElement {
    /**
     * Global defaults — override per-element via `data-aos-*` attributes.
     * Change these to adjust behaviour project-wide without touching every call-site.
     *
     * @type {{
     *   offset: number,
     *   duration: number,
     *   delay: number,
     *   easing: string,
     *   once: boolean,
     *   threshold: number,
     *   breakpoint: number,
     *   animation: string,
     * }}
     */
    static defaults = {
      offset: 200,          // px shrunk from viewport bottom (IntersectionObserver rootMargin)
      duration: 600,        // transition duration, ms
      delay: 0,             // transition delay on enter, ms
      easing: 'ease',       // CSS transition-timing-function
      once: true,           // unobserve after first trigger
      threshold: 0,         // IntersectionObserver threshold (0–1)
      breakpoint: 1023,     // px — window.innerWidth ≤ this is "small screen"
      animation: 'fade-up', // fallback data-aos value
    };

    constructor() {
      super();

      /** @type {IntersectionObserver | null} */
      this._observer = null;
      /** @type {MediaQueryList | null} */
      this._mediaQuery = null;
      /** @type {(() => void) | null} */
      this._mediaQueryHandler = null;
      /** @type {ReturnType<WLSNAOSObserver.prototype._buildSettings> | null} */
      this._settings = null;
    }

    connectedCallback() {
      this._settings = this._buildSettings();
      this._applyAnimation(this._resolveAnimation());
      this._applyStyles();
      this._setupObserver();
      this._setupBreakpointWatcher();
      this.classList.add('aos-init');
      this.setAttribute('data-wlsn-hydrated', '');
    }

    disconnectedCallback() {
      this._observer?.disconnect();
      this._observer = null;

      if (this._mediaQuery && this._mediaQueryHandler) {
        this._mediaQuery.removeEventListener('change', this._mediaQueryHandler);
        this._mediaQuery = null;
        this._mediaQueryHandler = null;
      }
    }

    // ── Settings ──────────────────────────────────────────────────────────────

    /**
     * Reads all `data-aos-*` attributes on the host, falling back to `WLSNAOSObserver.defaults`.
     * Called once in `connectedCallback`; result is stored in `this._settings`.
     */
    _buildSettings() {
      const d = WLSNAOSObserver.defaults;

      /**
       * @param {string} attrName
       * @param {string | number | boolean} fallback
       * @returns {string}
       */
      const attr = (attrName, fallback) => {
        const val = this.getAttribute(attrName);
        return val !== null ? val : String(fallback);
      };

      return {
        offset:           Number(attr('data-aos-offset',     d.offset)),
        duration:         Number(attr('data-aos-duration',   d.duration)),
        delay:            Number(attr('data-aos-delay',      d.delay)),
        easing:                  attr('data-aos-easing',     d.easing),
        once:                    attr('data-aos-once',       String(d.once)) !== 'false',
        threshold:        Number(attr('data-aos-threshold',  d.threshold)),
        breakpoint:       Number(attr('data-aos-breakpoint', d.breakpoint)),
        animation:               attr('data-aos',            d.animation),
        animationDesktop: this.getAttribute('data-aos-desktop'),
        animationMobile:  this.getAttribute('data-aos-mobile'),
      };
    }

    /**
     * Returns the animation name for the current viewport width.
     * Priority: desktop override → mobile override → base animation.
     *
     * @returns {string}
     */
    _resolveAnimation() {
      const { breakpoint, animation, animationDesktop, animationMobile } = this._settings;
      const isSmall = window.innerWidth <= breakpoint;

      if (!isSmall && animationDesktop) return animationDesktop;
      if (isSmall  && animationMobile)  return animationMobile;
      return animation;
    }

    // ── DOM ───────────────────────────────────────────────────────────────────

    /**
     * Writes the resolved animation name to `data-aos`.
     * `wlsn-aos-observer.css` uses this attribute for the initial hidden transform/opacity state.
     *
     * @param {string} name  e.g. `fade-up`, `zoom-in`, `flip-up`.
     */
    _applyAnimation(name) {
      this.setAttribute('data-aos', name);
    }

    /**
     * Applies duration and easing as inline styles so any value is supported —
     * not just the 50 ms-stepped values baked into `wlsn-aos-observer.css`.
     * `transitionDelay` starts at 0; it is set to the real delay only inside `_animate()`.
     */
    _applyStyles() {
      const { duration, easing } = this._settings;
      this.style.transitionDuration       = `${duration}ms`;
      this.style.transitionTimingFunction = easing;
      this.style.transitionDelay          = '0ms';
    }

    // ── IntersectionObserver ─────────────────────────────────────────────────

    /**
     * Creates (or re-creates) an IntersectionObserver.
     *
     * Negative bottom `rootMargin` replicates AOS `offset` behaviour:
     * the element must be `offset`px inside the viewport from the bottom before it triggers.
     *
     * `safeOffset` is capped to `viewport height − 1` to ensure the element can
     * always intersect even if it is shorter than `offset`.
     */
    _setupObserver() {
      this._observer?.disconnect();

      const { offset, threshold, once } = this._settings;
      const safeOffset = Math.min(offset, Math.max(window.innerHeight - 1, 0));

      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this._animate();
              if (once) this._observer.unobserve(this);
            } else if (entry.boundingClientRect.bottom <= 0) {
              // Element is entirely above the viewport — already scrolled past on load.
              // Show it instantly without playing the animation.
              this._showInstantly();
              if (once) this._observer.unobserve(this);
            } else if (!once) {
              this._unanimate();
            }
          });
        },
        {
          rootMargin: `0px 0px -${safeOffset}px 0px`,
          threshold,
        }
      );

      this._observer.observe(this);
    }

    // ── Breakpoint watcher ────────────────────────────────────────────────────

    /**
     * Listens for the configured breakpoint crossing via `matchMedia`.
     * Only wired when at least one of `animationDesktop` / `animationMobile` is set.
     * Resets animation state, swaps `data-aos`, and re-creates the observer on change.
     */
    _setupBreakpointWatcher() {
      const { breakpoint, animationDesktop, animationMobile } = this._settings;
      if (!animationDesktop && !animationMobile) return;

      this._mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);

      this._mediaQueryHandler = () => {
        const next = this._resolveAnimation();
        if (this.getAttribute('data-aos') === next) return;

        this._unanimate();
        this._applyAnimation(next);
        this._setupObserver();
      };

      this._mediaQuery.addEventListener('change', this._mediaQueryHandler);
    }

    // ── Animation state ───────────────────────────────────────────────────────

    /**
     * Transitions element to its visible/resting state by adding `aos-animate`.
     * Delay is applied inline only on enter so the reverse is always immediate.
     */
    _animate() {
      const { delay } = this._settings;
      if (delay > 0) this.style.transitionDelay = `${delay}ms`;
      this.classList.add('aos-animate');
    }

    /**
     * Snaps element to its final visible state with no transition.
     * Used when the element is already above the viewport on page load — the user
     * has scrolled past it, so playing the animation would be jarring or impossible to see.
     * Transition duration is restored after one frame so future re-animations (once: false) work normally.
     */
    _showInstantly() {
      this.style.transitionDuration = '0ms';
      this.style.transitionDelay    = '0ms';
      this.classList.add('aos-animate');
      requestAnimationFrame(() => {
        this.style.transitionDuration = `${this._settings.duration}ms`;
      });
    }

    /**
     * Reverts element to its initial hidden state by removing `aos-animate`.
     * Clears delay so the out-transition fires without lag.
     */
    _unanimate() {
      this.style.transitionDelay = '0ms';
      this.classList.remove('aos-animate');
    }
  }

  customElements.define('wlsn-aos-observer', WLSNAOSObserver);
}
